import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Target, Users, Zap, ArrowRight, TrendingUp, Award } from 'lucide-react';
import { Footer } from '../shared/components/Footer';
import { AuthButton } from '../components/auth';

// Tool card data - Add new tools here
const tools = [
  {
    id: 'linkedin-profile-optimizer',
    name: 'LinkedIn Profile Optimizer',
    description: 'Complete LinkedIn profile analysis and optimization. Get actionable insights to improve visibility, engagement, and professional impact.',
    iconSrc: '/TransOptimizerIcon.png',
    path: 'https://optimizer.growleads.io',
    isExternal: true,
    features: ['Advanced Analysis', 'Personalised Suggestions', 'Search Optimized'],
    isNew: false,
    isPopular: true,
    popularLabel: 'Most Loved',
  },
  {
    id: 'linkedin-profile-picture-generator',
    name: 'Profile Picture Generator',
    description: 'Transform your photo into a professional, LinkedIn ready headshot that builds trust and credibility.',
    iconSrc: '/profile-tool-trans.png',
    path: '/linkedin-profile-picture-generator',
    isExternal: false,
    features: ['100% Accuracy', 'Personalisation Capability', 'Studio Quality'],
    isNew: true,
    isPopular: false,
  },
  {
    id: 'linkedin-banner-generator',
    name: 'LinkedIn Banner Generator',
    description: 'Create attracive, professional and value driven LinkedIn banners that builds your authority.',
    iconSrc: '/Banner trans.png',
    path: '/linkedin-banner-generator',
    isExternal: false,
    features: ['Hyper Personalised', 'Professionally Designed', 'High Resolution'],
    isNew: false,
    isPopular: true,
  },
  {
    id: 'linkedin-headline-generator',
    name: 'LinkedIn Headline Generator',
    description: 'Generate optimized, attention-grabbing LinkedIn headlines tailored to your role, industry, and goals.',
    iconSrc: '/headline-tool-tans.png',
    path: '/linkedin-headline-generator',
    isExternal: false,
    features: ['LinkedIn Optimized', 'Highly Attractive', 'SEO Optimized'],
    isNew: false,
    isPopular: true,
  },
  // Future tools template:
  // {
  //   id: 'linkedin-summary-generator',
  //   name: 'LinkedIn Summary Generator',
  //   description: 'Create compelling LinkedIn summaries that showcase your expertise and attract opportunities.',
  //   icon: FileText,
  //   path: '/linkedin-summary-generator',
  //   gradient: 'from-purple-500 to-pink-500',
  //   features: ['AI-Powered', 'Multiple Styles', 'Professional'],
  //   isNew: true,
  //   isPopular: false,
  // },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="https://growleads.io" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
              <img 
                src="/tools-trans.png" 
                alt="Growleads Tools" 
                className="h-9 w-auto object-contain"
              />
              <span className="font-display font-semibold text-foreground text-lg hidden sm:block">LinkedIn Tools</span>
            </a>
            <AuthButton />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-screen">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted/30" />
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col items-center justify-center py-16 lg:py-20">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <img src="/tools-trans.png" alt="Growleads Tools" className="w-4 h-4 object-contain" />
              <span className="text-sm font-medium text-primary">Free LinkedIn Tools by Growleads.io</span>
            </div>
            
            {/* Main Heading */}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-5">
              Power Up Your{' '}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                LinkedIn Presence
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Free AI-powered tools to optimize your LinkedIn profile, generate compelling content, 
              and attract more opportunities. Built for founders, professionals, and growth-minded individuals.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => document.querySelector('#tools')?.scrollIntoView({ behavior: 'smooth' })}
                className="group flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/25"
              >
                Try our LinkedIn Tools
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a 
                href="https://growleads.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-4 bg-card border border-border rounded-xl font-semibold text-foreground hover:bg-[#60C5FE] hover:border-[#60C5FE] hover:text-white transition-all"
              >
                Visit Growleads.io
              </a>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-12 max-w-4xl mx-auto w-full">
            {[
              { value: '1K+', label: 'Happy Users' },
              { value: '100%', label: 'Free Forever' },
              { value: '< 10s', label: 'Generation Time' },
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center bg-card/80 backdrop-blur-sm border border-border rounded-2xl px-6 py-4 shadow-sm"
              >
                <div className="text-3xl font-bold text-foreground leading-none">{stat.value}</div>
                <div className="mt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section id="tools" className="py-16 bg-gradient-to-b from-background to-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Our Free LinkedIn Tools
            </h2>
            <p className="text-lg text-muted-foreground mx-auto sm:whitespace-nowrap">
              Professional tools to help you stand out on LinkedIn and grow your network.
            </p>
          </div>
          
          {/* Active Tools Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {tools.map((tool) => {
              const CardContent = () => (
                <>
                  {/* Badges */}
                  <div className="absolute top-4 right-4 flex gap-2">
                    {tool.isNew && (
                      <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                        New
                      </span>
                    )}
                    {tool.isPopular && (
                      <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                        {tool.popularLabel || 'Popular'}
                      </span>
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    {tool.iconSrc ? (
                      <img src={tool.iconSrc} alt="" className="w-7 h-7 object-contain" />
                    ) : (
                      <tool.icon className="w-7 h-7 text-primary" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-display text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {tool.description}
                  </p>
                  
                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {tool.features.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1.5 text-xs bg-card border border-border rounded-lg shadow-sm text-foreground font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                  
                  {/* Arrow */}
                  <div className="flex items-center text-primary font-medium text-sm">
                    Try Now
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </>
              );

              return tool.isExternal ? (
                <a
                  key={tool.id}
                  href={tool.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative bg-card border border-border rounded-2xl p-6 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
                >
                  <CardContent />
                </a>
              ) : (
                <Link
                  key={tool.id}
                  to={tool.path}
                  className="group relative bg-card border border-border rounded-2xl p-6 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
                >
                  <CardContent />
                </Link>
              );
            })}
          </div>
          
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Why Choose Our Tools?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Zap,
                title: 'Lightning Fast',
                description: 'Get results in seconds, not minutes. Our AI generates content instantly.',
              },
              {
                icon: Target,
                title: 'Goal-Oriented',
                description: 'Tailored to your specific objectives, whether its getting clients or job offers.',
              },
              {
                icon: TrendingUp,
                title: 'SEO Optimized',
                description: 'Built with LinkedIn search algorithms in mind for maximum visibility.',
              },
              {
                icon: Award,
                title: '1 Free Credit',
                description: 'Get 1 free credit on signup. No credit card required to start.',
              },
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer
        logoSrc="/tools-trans.png"
        brandLabel="Tools by Growleads.io"
        footerLinkHref="https://optimizer.growleads.io"
        footerLinkText="LinkedIn Profile Optimizer"
      />
    </div>
  );
};

export default LandingPage;
